import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useHousehold } from "@/context/HouseholdContext";
import { api, formatApiError } from "@/lib/api";
import { useFormat } from "@/lib/format";

const W = [["active", "Actives", "tone-mint"], ["soon", "Bientôt expirées", "tone-butter"], ["expired", "Expirées", "tone-rose"], ["none", "Non renseignées", "tone-sky"]];

export const useInventory = () => {
  const { active } = useHousehold();
  return useQuery({ queryKey: ["assets", active?.id, "inventory"], queryFn: () => api.get(`/households/${active.id}/inventory`).then((r) => r.data), enabled: !!active });
};

const Kpi = ({ label, value, testid, sub }) => (
  <div><div className="text-xs text-muted-foreground">{label}</div><div className="num text-2xl font-bold" data-testid={testid}>{value}</div>{sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}</div>
);

export const InventorySummary = () => {
  const { active } = useHousehold();
  const { money } = useFormat();
  const { data: d } = useInventory();
  const [busy, setBusy] = useState(false);
  if (!d?.count) return null;
  const pdf = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/households/${active.id}/inventory/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      Object.assign(document.createElement("a"), { href: url, download: "inventaire-cashmy.pdf" }).click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const loss = d.purchase_total ? Math.round((1 - d.current_total / d.purchase_total) * 100) : 0;
  return (
    <div className="card-surface space-y-4 p-5" data-testid="inventory-summary">
      <div className="flex flex-wrap items-start gap-4">
        <div className="grid flex-1 basis-72 grid-cols-2 gap-4 sm:grid-cols-3">
          <Kpi label="Valeur d'achat" value={money(d.purchase_total)} testid="inventory-purchase-total" sub={`${d.count} équipements`} />
          <Kpi label="Valeur actuelle estimée" value={money(d.current_total)} testid="inventory-current-total" sub={`−${loss} % depuis l'achat`} />
          <Kpi label="Sans facture liée" value={d.missing_invoice} testid="inventory-missing-invoice" sub="à ajouter dans Documents" />
        </div>
        <Button variant="outline" className="rounded-full" onClick={pdf} disabled={busy} data-testid="inventory-pdf-button">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />}Export PDF pour l'assurance
        </Button>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="inventory-warranty">
        <span className="text-xs font-semibold text-muted-foreground">Garanties :</span>
        {W.map(([k, l, tone]) => <span key={k} className={`tone-chip ${tone} rounded-full px-2.5 py-0.5 text-xs font-semibold`} data-testid={`inventory-warranty-${k}`}>{d.warranty[k]} {l.toLowerCase()}</span>)}
      </div>
    </div>
  );
};
